import { polyArea } from '../../shared/geom.js';
import { hashUnit } from '../../shared/rng.js';
import type { Area, PitchSport } from '../../shared/types.js';
import { Frame, MESH, orientedBox, type ObjectBuilder, type ObjectContext } from './cityObjects.js';

/**
 * Sports pitches with their lines painted on, goals, hoops and nets standing on them, and a
 * fence round the courts.
 *
 * A pitch is drawn on a plate of its own rather than on the area fill under it. Paint has to
 * lie on a surface, and the fill is draped over the terrain with up to a third of a metre of
 * slack and lifted by whatever it sags (`addDrapedPolygon` in `worldMesh.ts`), so a line
 * drawn at the terrain height was under the grass on one side of a pitch and floating over
 * it on the other. A real pitch is levelled anyway: the plate is flat, at the highest ground
 * under it, with a kerb down to the ground round it — which on a slope is what a pitch cut
 * into a hillside looks like.
 *
 * Only rectangles are marked, at the regulation size where that fits and scaled down where it
 * does not, and only for the sports whose markings are drawn (`PitchSport`). A pitch of any
 * other shape keeps its coloured fill and nothing else: lines guessed onto a pentagon are
 * worse than none.
 */

const LINE = 0xeeede6;
const POST = 0xf2f2ee;
const FENCE_COLOURS = [0x2f4b3a, 0x8a9094, 0x34503f];
const NET = 0x1d2124;
const KERB = 0x9a978f;
/** How rectangular a pitch must be to be marked: its area over its bounding box's. */
const MIN_FILL = 0.82;
/** Above the fill: it sits at 8 cm and may be lifted to 13 by the drape. */
const PLATE_LIFT = 0.16;
const PLATE_LIFT_FLAT = 0.1;
const PAINT = 0.015;

interface Court {
  /** Playing area, regulation, in metres: along the plate and across it. */
  length: number;
  width: number;
  /** Run-off the plate is expected to have round the lines, each side. */
  margin: number;
  fence: number;
}

const COURTS: Record<PitchSport, Court> = {
  soccer: { length: 105, width: 68, margin: 1.5, fence: 0 },
  handball: { length: 40, width: 20, margin: 1, fence: 4 },
  basketball: { length: 28, width: 15, margin: 1, fence: 4 },
  tennis: { length: 23.77, width: 10.97, margin: 1.5, fence: 3.6 },
  volleyball: { length: 18, width: 9, margin: 1.5, fence: 4 },
};

export function buildPitches(out: ObjectBuilder, ctx: ObjectContext): void {
  for (const area of ctx.world.areas) {
    if (area.kind !== 'pitch' || !area.sport) continue;
    const box = orientedBox(area.poly);
    if (!box) continue;
    const L = box.halfLength;
    const W = box.halfWidth;
    if (W < 3 || L > 75) continue;
    if (Math.abs(polyArea(area.poly)) / (4 * L * W) < MIN_FILL) continue;
    pitch(out, ctx, area, area.sport, box);
  }
}

function pitch(
  out: ObjectBuilder,
  ctx: ObjectContext,
  area: Area,
  sport: PitchSport,
  box: { cx: number; cz: number; ux: number; uz: number; halfLength: number; halfWidth: number },
): void {
  const L = box.halfLength;
  const W = box.halfWidth;
  const corners: [number, number][] = [[-L, -W], [L, -W], [L, W], [-L, W]];
  const probe = new Frame(box.cx, box.cz, box.ux, box.uz, 0);
  let hi = -Infinity;
  let lo = Infinity;
  for (const [a, b] of [...corners, [0, 0], [0, -W], [0, W], [-L, 0], [L, 0]] as [number, number][]) {
    const h = ctx.terrain.heightAt(probe.x(a, b), probe.z(a, b));
    hi = Math.max(hi, h);
    lo = Math.min(lo, h);
  }
  const y = hi + (ctx.terrain.isFlat ? PLATE_LIFT_FLAT : PLATE_LIFT);
  const f = new Frame(box.cx, box.cz, box.ux, box.uz, y);

  // The plate, in mown stripes where it is grass.
  const g = (area.color >> 8) & 0xff;
  const grass = g > ((area.color >> 16) & 0xff) + 10 && g > (area.color & 0xff) + 10;
  const bands = grass ? Math.max(1, Math.round((2 * L) / 6)) : 1;
  for (let k = 0; k < bands; k++) {
    const a0 = -L + (2 * L * k) / bands;
    const a1 = -L + (2 * L * (k + 1)) / bands;
    out.paint(grass && k % 2 ? shadeHex(area.color, 1.1) : area.color);
    out.quad([...f.at(a0, -W, 0), ...f.at(a1, -W, 0), ...f.at(a1, W, 0), ...f.at(a0, W, 0)], [0, 1, 0]);
  }
  // The kerb down to the ground on each side.
  out.paint(KERB);
  for (let i = 0; i < 4; i++) {
    const [a0, b0] = corners[i];
    const [a1, b1] = corners[(i + 1) % 4];
    const top0 = f.at(a0, b0, 0);
    const top1 = f.at(a1, b1, 0);
    const down = lo - 0.1 - y;
    const bot0 = f.at(a0, b0, down);
    const bot1 = f.at(a1, b1, down);
    const mid = f.at((a0 + a1) / 2, (b0 + b1) / 2, 0);
    out.quad([...bot0, ...bot1, ...top1, ...top0], [mid[0] - box.cx, 0, mid[2] - box.cz]);
  }

  const court = COURTS[sport];
  // The marked area: regulation where it fits, the plate less its run-off where it does not.
  const room = (half: number, want: number) => Math.min(want, 2 * half - 2 * Math.min(court.margin, half * 0.15));
  let length = room(L, court.length);
  let width = room(W, court.width);
  if (sport === 'soccer') {
    // A football pitch is whatever size the ground is, within the laws' limits.
    length = 2 * L - 2 * Math.min(court.margin, L * 0.06);
    width = 2 * W - 2 * Math.min(court.margin, W * 0.08);
  }
  if (length < 6 || width < 4) return;
  const lines = new Lines(out, f, PAINT, sport === 'soccer' ? 0.12 : 0.07);

  switch (sport) {
    case 'soccer':
      soccer(out, f, lines, length, width);
      break;
    case 'handball':
      handball(out, f, lines, length, width);
      break;
    case 'basketball':
      basketball(out, f, lines, length, width, L);
      break;
    case 'tennis': {
      // A club maps several courts as one polygon as often as not.
      const count = Math.max(1, Math.min(6, Math.floor((2 * W) / (court.width + 5))));
      for (let k = 0; k < count; k++) {
        const b = -W + ((k + 0.5) * 2 * W) / count;
        tennis(out, new Frame(f.x(0, b), f.z(0, b), f.ux, f.uz, y), new Lines(out, new Frame(f.x(0, b), f.z(0, b), f.ux, f.uz, y), PAINT, 0.06), length, Math.min(width, (2 * W) / count - 2));
      }
      break;
    }
    case 'volleyball':
      volleyball(out, f, lines, length, width);
      break;
  }

  const small = sport !== 'soccer' || length < 70;
  const fence = small ? (court.fence || 4) : 0;
  const look = hashUnit(ctx.world.seed, area.id ^ 0x2f6b9d1);
  const colour = FENCE_COLOURS[Math.floor(look * FENCE_COLOURS.length) % FENCE_COLOURS.length];
  if (fence) {
    for (let i = 0; i < 4; i++) {
      const [a0, b0] = corners[i];
      const [a1, b1] = corners[(i + 1) % 4];
      fenceRun(out, f, a0 * 0.995, b0 * 0.99, a1 * 0.995, b1 * 0.99, fence, colour);
    }
  } else {
    // A full-size ground has ball-stop nets behind the goals and nothing along the sides.
    for (const end of [-1, 1]) {
      fenceRun(out, f, end * L * 0.995, -W * 0.5, end * L * 0.995, W * 0.5, 6, colour);
    }
  }
}

/** Lines of paint in a pitch's frame, at its plate. */
class Lines {
  constructor(
    private readonly out: ObjectBuilder,
    private readonly f: Frame,
    private readonly lift: number,
    private readonly width: number,
  ) {}

  line(a0: number, b0: number, a1: number, b1: number): void {
    this.out.paint(LINE);
    const p = this.f.at(a0, b0, this.lift);
    const q = this.f.at(a1, b1, this.lift);
    this.out.stripe(p[0], p[1], p[2], q[0], q[1], q[2], this.width);
  }

  rect(a0: number, b0: number, a1: number, b1: number): void {
    this.line(a0, b0, a1, b0);
    this.line(a1, b0, a1, b1);
    this.line(a1, b1, a0, b1);
    this.line(a0, b1, a0, b0);
  }

  arc(ca: number, cb: number, r: number, from: number, to: number, segments: number, clip = Infinity): void {
    for (let i = 0; i < segments; i++) {
      const t0 = from + ((to - from) * i) / segments;
      const t1 = from + ((to - from) * (i + 1)) / segments;
      const b0 = cb + Math.sin(t0) * r;
      const b1 = cb + Math.sin(t1) * r;
      if (Math.abs(b0) > clip || Math.abs(b1) > clip) continue;
      this.line(ca + Math.cos(t0) * r, b0, ca + Math.cos(t1) * r, b1);
    }
  }
}

function soccer(out: ObjectBuilder, f: Frame, lines: Lines, length: number, width: number): void {
  const hl = length / 2;
  const hw = width / 2;
  const k = Math.max(0.3, Math.min(1, length / 105));
  lines.rect(-hl, -hw, hl, hw);
  lines.line(0, -hw, 0, hw);
  lines.arc(0, 0, 9.15 * k, 0, Math.PI * 2, 20);
  const goal = length >= 80 ? 7.32 : length >= 45 ? 5 : 3;
  const tall = goal > 7 ? 2.44 : 2;
  for (const end of [-1, 1]) {
    const box = Math.min(40.32 * k, width * 0.7) / 2;
    const deep = 16.5 * k;
    const six = Math.min(18.32 * k, width * 0.36) / 2;
    lines.line(end * hl, -box, end * (hl - deep), -box);
    lines.line(end * (hl - deep), -box, end * (hl - deep), box);
    lines.line(end * (hl - deep), box, end * hl, box);
    lines.line(end * hl, -six, end * (hl - 5.5 * k), -six);
    lines.line(end * (hl - 5.5 * k), -six, end * (hl - 5.5 * k), six);
    lines.line(end * (hl - 5.5 * k), six, end * hl, six);
    // The penalty spot, as a short dash.
    lines.line(end * (hl - 11 * k) - 0.12, 0, end * (hl - 11 * k) + 0.12, 0);
    goalFrame(out, f, end * hl, end, goal, tall);
  }
}

function handball(out: ObjectBuilder, f: Frame, lines: Lines, length: number, width: number): void {
  const hl = length / 2;
  const hw = width / 2;
  const k = Math.max(0.4, Math.min(1, length / 40));
  lines.rect(-hl, -hw, hl, hw);
  lines.line(0, -hw, 0, hw);
  for (const end of [-1, 1]) {
    // The six-metre line, round from post to post, as one arc about the goal's centre.
    lines.arc(end * hl, 0, 6 * k, end > 0 ? Math.PI / 2 : -Math.PI / 2, end > 0 ? Math.PI * 1.5 : Math.PI / 2, 10, hw);
    goalFrame(out, f, end * hl, end, 3, 2);
  }
}

/** Posts, a crossbar, stays back to the ground and a net over the back and sides. */
function goalFrame(out: ObjectBuilder, f: Frame, a: number, end: number, width: number, tall: number): void {
  const hw = width / 2;
  const back = a + end * Math.min(1.8, tall * 0.75);
  out.paint(POST);
  for (const b of [-hw, hw]) {
    out.beam(...f.at(a, b, 0), ...f.at(a, b, tall), 0.12);
    out.beam(...f.at(a, b, tall), ...f.at(back, b, 0), 0.06);
  }
  out.beam(...f.at(a, -hw, tall), ...f.at(a, hw, tall), 0.12);
  out.beam(...f.at(back, -hw, 0.03), ...f.at(back, hw, 0.03), 0.06);
  out.paint(0xe8e8e2, MESH);
  out.sheet([...f.at(a, -hw, tall), ...f.at(a, hw, tall), ...f.at(back, hw, 0), ...f.at(back, -hw, 0)], [f.ux * end, 0, f.uz * end]);
  for (const b of [-hw, hw]) {
    const p = f.at(a, b, 0);
    const q = f.at(back, b, 0);
    const r = f.at(a, b, tall);
    out.sheet([...p, ...q, ...r, ...r], [f.vx * Math.sign(b), 0, f.vz * Math.sign(b)]);
  }
}

function basketball(out: ObjectBuilder, f: Frame, lines: Lines, length: number, width: number, plateHalf: number): void {
  const hl = length / 2;
  const hw = width / 2;
  const k = Math.max(0.5, Math.min(1, length / 28));
  lines.rect(-hl, -hw, hl, hw);
  lines.line(0, -hw, 0, hw);
  lines.arc(0, 0, 1.8 * k, 0, Math.PI * 2, 16);
  for (const end of [-1, 1]) {
    const key = 4.9 * k / 2;
    const ft = hl - 5.8 * k;
    lines.rect(end * hl, -key, end * ft, key);
    lines.arc(end * ft, 0, 1.8 * k, end > 0 ? Math.PI / 2 : -Math.PI / 2, end > 0 ? Math.PI * 1.5 : Math.PI / 2, 8);
    // The three-point line, clipped where it meets the straight runs along the sides.
    const hoop = hl - 1.575 * k;
    const r = 6.75 * k;
    const side = Math.min(hw - 0.9 * k, r * 0.98);
    lines.arc(end * hoop, 0, r, end > 0 ? Math.PI / 2 : -Math.PI / 2, end > 0 ? Math.PI * 1.5 : Math.PI / 2, 16, side);
    const reach = Math.sqrt(Math.max(0, r * r - side * side));
    for (const s of [-side, side]) lines.line(end * hl, s, end * (hoop - reach), s);
    hoopStand(out, f, end, hl, Math.max(hl + 0.4, Math.min(hl + 1.0, plateHalf - 0.2)), k);
  }
}

function hoopStand(out: ObjectBuilder, f: Frame, end: number, baseline: number, pole: number, k: number): void {
  const board = end * (baseline - 1.2 * k);
  const ring = end * (baseline - 1.2 * k - 0.38);
  out.paint(0x3b4046);
  out.beam(...f.at(end * pole, 0, 0), ...f.at(end * pole, 0, 3.4), 0.14);
  out.beam(...f.at(end * pole, 0, 3.3), ...f.at(board, 0, 3.3), 0.09);
  out.paint(POST).box(f, board - 0.03, board + 0.03, -0.9, 0.9, 2.9, 3.95, true);
  out.paint(0xc95220);
  const segments = 8;
  for (let i = 0; i < segments; i++) {
    const t0 = (i / segments) * Math.PI * 2;
    const t1 = ((i + 1) / segments) * Math.PI * 2;
    out.beam(
      ...f.at(ring + Math.cos(t0) * 0.23, Math.sin(t0) * 0.23, 3.05),
      ...f.at(ring + Math.cos(t1) * 0.23, Math.sin(t1) * 0.23, 3.05),
      0.03,
    );
  }
}

function tennis(out: ObjectBuilder, f: Frame, lines: Lines, length: number, width: number): void {
  const k = Math.min(1, length / 23.77, width / 10.97);
  const hl = (23.77 * k) / 2;
  const hw = (10.97 * k) / 2;
  const singles = (8.23 * k) / 2;
  const service = 6.4 * k;
  lines.rect(-hl, -hw, hl, hw);
  lines.line(-hl, -singles, hl, -singles);
  lines.line(-hl, singles, hl, singles);
  lines.line(-service, -singles, -service, singles);
  lines.line(service, -singles, service, singles);
  lines.line(-service, 0, service, 0);
  for (const end of [-1, 1]) lines.line(end * hl, 0, end * (hl - 0.2), 0);
  net(out, f, hw + 0.914 * k, 0, 0.914, 1.07);
}

function volleyball(out: ObjectBuilder, f: Frame, lines: Lines, length: number, width: number): void {
  const k = Math.min(1, length / 18, width / 9);
  const hl = (18 * k) / 2;
  const hw = (9 * k) / 2;
  lines.rect(-hl, -hw, hl, hw);
  lines.line(0, -hw, 0, hw);
  for (const end of [-1, 1]) lines.line(end * 3 * k, -hw, end * 3 * k, hw);
  net(out, f, hw + 1, 1.43, 2.43, 2.55);
}

/** A net across the middle, on two posts: mesh, with the white tape along its top. */
function net(out: ObjectBuilder, f: Frame, half: number, bottom: number, top: number, posts: number): void {
  out.paint(0x3b4046);
  for (const b of [-half, half]) out.beam(...f.at(0, b, 0), ...f.at(0, b, posts), 0.08);
  out.paint(NET, MESH);
  out.sheet([...f.at(0, -half, bottom), ...f.at(0, half, bottom), ...f.at(0, half, top - 0.05), ...f.at(0, -half, top - 0.05)], [f.ux, 0, f.uz]);
  out.paint(POST).box(f, -0.02, 0.02, -half, half, top - 0.06, top);
}

/** A run of chain-link fence: posts every three metres, a rail top and bottom, mesh between. */
function fenceRun(out: ObjectBuilder, f: Frame, a0: number, b0: number, a1: number, b1: number, height: number, colour: number): void {
  const p0 = f.at(a0, b0, 0);
  const p1 = f.at(a1, b1, 0);
  const len = Math.hypot(p1[0] - p0[0], p1[2] - p0[2]);
  if (len < 0.5) return;
  const posts = Math.max(1, Math.ceil(len / 3));
  out.paint(colour);
  for (let i = 0; i <= posts; i++) {
    const t = i / posts;
    const x = p0[0] + (p1[0] - p0[0]) * t;
    const z = p0[2] + (p1[2] - p0[2]) * t;
    out.beam(x, p0[1] - 0.1, z, x, p0[1] + height, z, 0.07);
  }
  out.beam(p0[0], p0[1] + height, p0[2], p1[0], p1[1] + height, p1[2], 0.05);
  out.beam(p0[0], p0[1] + 0.08, p0[2], p1[0], p1[1] + 0.08, p1[2], 0.05);
  const nx = (p1[2] - p0[2]) / len;
  const nz = -(p1[0] - p0[0]) / len;
  out.paint(colour, MESH);
  out.sheet([p0[0], p0[1], p0[2], p1[0], p1[1], p1[2], p1[0], p1[1] + height, p1[2], p0[0], p0[1] + height, p0[2]], [nx, 0, nz]);
}

function shadeHex(color: number, k: number): number {
  const ch = (shift: number) => Math.min(255, Math.round(((color >> shift) & 0xff) * k));
  return (ch(16) << 16) | (ch(8) << 8) | ch(0);
}
